import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import { basemapInkFor } from '~/client/src/shared/map/labelLayerStyle'
import ClosuresStore from '~/client/src/shared/stores/domain/Closures.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { IGroundContext } from '../../MapBoxViewer/labelGroundTone'
import MapBoxViewerStore from '../../MapBoxViewer/MapBoxViewer.store'
import MapViewItemDrawnPart from '../models/SitemapItemDrawnPart'
import { LocationMarkerPart } from '../utils/GLLocationMarkerLayer'
import { ILocationClosureContext } from '../utils/locationClosureState'
import { IPlanFootprint } from '../utils/planFootprints'
import { ILocationLayerDisplay, MapLocationLayers } from './MapLocationLayers'

interface IProps {
  items: MapViewItemBase[]
  mapBoxViewerStore: MapBoxViewerStore
  onSelectItem?: (item: MapViewItemBase) => void
  selectSitemapItemDrawnPart?: (part: MapViewItemDrawnPart) => void
  // What the labels stand on, so an unplated one can pick ink that reads against it. Hosts
  // that cannot describe their ground omit it and every unplated label takes the basemap guess.
  ground?: IGroundContext
  // Where the plan sheets are, so a location label on one is plated regardless of its checkbox.
  planFootprints?: IPlanFootprint[]
  // The item under edit. It stays in these layers and gains a ring, instead of being pulled out
  // and redrawn by the editor.
  outlinedItemId?: string
  shouldHideOutline?: boolean
  // The item under edit when this view hides it, drawn faded with an eye-off badge.
  offViewItemId?: string
  // Paints the location whose card is open above the pins. A host that lifts passes its own
  // click targets to yield to.
  liftsSelection?: boolean
  liftedItemId?: string
  yieldToLayerIds?: string[]
  // Which location parts draw. Unset draws everything.
  display?: ILocationLayerDisplay
  // Locations on identical coordinates draw as one marker, whose click lists them here.
  onClickStack?: (stack: mapboxgl.GeoJSONFeature) => void

  className?: string

  isDisabled?: boolean
  selectedStartDate?: Date
  selectedEndDate?: Date
  shouldHideRestrictions?: boolean

  closuresStore?: ClosuresStore
  projectDateStore?: ProjectDateStore
  state?: InitialState
}

// The app's adapter onto MapLocationLayers: store-shaped inputs in, no drawing of its own.
@inject('closuresStore', 'projectDateStore', 'state')
@observer
export default class GlobeViewItems extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    makeObservable(this)
  }

  @action
  public componentDidUpdate(prevProps: IProps): void {
    this.syncLocalProps()
    if (this.props.isDisabled && !prevProps.isDisabled) {
      this.hoverItem(null)
    }
  }

  public render() {
    const { mapBoxViewerStore } = this.localProps
    // Straight from props, NOT through the localProps mirror. `ground` and `planFootprints` are
    // fresh objects per recompute and localProps is observable.deep, so mirroring them would
    // deep-observe every fill ring and write on every update — re-rendering the host, which
    // recomputes, which writes again. Reading own props in own render is safe; the mirror exists
    // for reading them inside a FOREIGN derivation.
    //
    // `items` and the ids travel together because syncLocalProps runs in componentDidUpdate: a
    // mirrored value lands one render AFTER an unmirrored one, and two values that must agree
    // cannot be split across the two. Splitting them left copies at stale positions that nothing
    // invalidated. `items` feeds no @computed, which is the only thing the mirror protects.
    const {
      items,
      ground,
      planFootprints,
      outlinedItemId,
      shouldHideOutline,
      offViewItemId,
      liftsSelection,
      liftedItemId,
      yieldToLayerIds,
      onClickStack,
      isDisabled,
      display,
    } = this.props

    return (
      <MapLocationLayers
        items={items}
        isProjectMarkerOnly={mapBoxViewerStore.shouldShowProjectMarker}
        projectCoordinates={this.projectCoordinates}
        closureContext={this.closureContext}
        highlightedItemId={mapBoxViewerStore.highlightedItemId}
        isZoomScalingEnabled={
          mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled
        }
        isLabelOverlapEnabled={
          mapBoxViewerStore.globeViewStyle.isLabelOverlapEnabled
        }
        ground={ground}
        basemapTone={basemapInkFor(mapBoxViewerStore.isSatelliteActive)}
        planFootprints={planFootprints}
        outlinedItemId={outlinedItemId}
        shouldHideOutline={shouldHideOutline}
        offViewItemId={offViewItemId}
        liftsSelection={liftsSelection}
        liftedItemId={liftedItemId}
        yieldToLayerIds={yieldToLayerIds}
        onHoverItem={isDisabled ? undefined : this.hoverItem}
        onClickItem={this.selectItemById}
        onClickStack={onClickStack && this.selectStack}
        onDoubleClickItem={this.editItemPartById}
        display={display}
      />
    )
  }

  // These feed @computeds, which can't read this.props, so they have to be mirrored.
  // Dates compare by value so a fresh Date each render doesn't invalidate the
  // marker source.
  private syncLocalProps(): void {
    const { selectedStartDate, selectedEndDate, shouldHideRestrictions } =
      this.props

    if (
      this.localProps.selectedStartDate?.getTime() !==
      selectedStartDate?.getTime()
    ) {
      this.localProps.selectedStartDate = selectedStartDate
    }
    if (
      this.localProps.selectedEndDate?.getTime() !== selectedEndDate?.getTime()
    ) {
      this.localProps.selectedEndDate = selectedEndDate
    }
    if (this.localProps.shouldHideRestrictions !== shouldHideRestrictions) {
      this.localProps.shouldHideRestrictions = shouldHideRestrictions
    }
  }

  @computed
  private get closureContext(): ILocationClosureContext {
    return {
      closuresStore: this.localProps.closuresStore,
      projectDateStore: this.localProps.projectDateStore,
      selectedStartDate: this.localProps.selectedStartDate,
      selectedEndDate: this.localProps.selectedEndDate,
      shouldHideRestrictions: this.localProps.shouldHideRestrictions,
    }
  }

  @computed
  private get projectCoordinates(): { latitude: number; longitude: number } {
    const center = this.localProps.state?.projectAddress?.center
    return center ? { latitude: center.lat, longitude: center.lng } : undefined
  }

  @action.bound
  private hoverItem(itemId: string | null): void {
    this.localProps.mapBoxViewerStore.hoveredMarkerItemId = itemId
  }

  // The whole list, not a copy of the layer's filter: a hit-test only reports ids it drew.
  private itemById(itemId: string): MapViewItemBase {
    return this.props.items.find(i => i.id === itemId)
  }

  // Re-selecting the item under edit deselects it first, which raises the save prompt.
  private isOutlined(itemId: string): boolean {
    return !!this.props.outlinedItemId && itemId === this.props.outlinedItemId
  }

  @action.bound
  private selectItemById(itemId: string): void {
    if (this.isOutlined(itemId)) return
    const item = this.itemById(itemId)
    if (item) this.selectItem(item)
  }

  @action.bound
  private selectStack(stack: mapboxgl.GeoJSONFeature): void {
    if (this.props.isDisabled) return
    this.props.onClickStack?.(stack)
  }

  // The part lands on the selected item, which is not the clicked one while selection is off
  // (plan alignment) or held by the save prompt.
  @action.bound
  private editItemPartById(itemId: string, part: LocationMarkerPart): void {
    if (this.props.isDisabled || !this.isOutlined(itemId)) {
      return
    }
    this.selectDrawnPartEditing(
      part === 'label' ? MapViewItemDrawnPart.Label : MapViewItemDrawnPart.Icon,
    )
  }

  private selectItem = (item: MapViewItemBase) => {
    const { onSelectItem, mapBoxViewerStore } = this.localProps

    if (!onSelectItem || this.props.isDisabled) {
      return
    }
    mapBoxViewerStore.selectedMarkerItemId = item.id
    onSelectItem(item)
  }

  private selectDrawnPartEditing = (part: MapViewItemDrawnPart) => {
    this.localProps.selectSitemapItemDrawnPart?.(part)
  }
}
